import * as analytics from 'analytics';

import { ApiErrorResponse, ApiService } from '@/features/api';
import titleMessageLinkModal from '@/legacy/modals/styled/titleMessageLink';
import * as companyService from '@/legacy/services/company';

import { API_PREFIX } from '../api/constants';
import XeroLogo from '../assets/xero-logo.svg';
import AccountingSoftwareCompany from '../types/AccountingSoftwareCompany';
import AccountingSoftwareContact from '../types/AccountingSoftwareContact';
import Bill from '../types/Bill';
import BillData from '../types/BillData';
import IAccount from '../types/IAccount';
import IBillAttachmentDetails from '../types/IBillAttachmentDetails';
import Invoice from '../types/Invoice';
import InvoiceData from '../types/InvoiceData';
import IOAuthRequest from '../types/IOAuthRequest';
import { SupportedAccountingSoftware } from '../types/SupportedAccountingSoftware';
import XeroAccountingSoftwareSettings from '../types/XeroAccountingSoftwareSettings';
import setupPopupWindow from '../utils/setupPopupWindow';

import BaseAccountingSoftwarePackage from './BaseAccountingSoftwarePackage';

class XeroAccountingPackage extends BaseAccountingSoftwarePackage {
  // TODO: remove vendor logo from public dir once we fully migrate to the new UI
  readonly logoUrl = `${import.meta.env.APP_STATIC_APP_ROOT_URL}/img/vendor/xero-icon.svg`;

  readonly icon = XeroLogo;

  supportsInvoices = true;

  constructor() {
    super(SupportedAccountingSoftware.Xero);
  }

  FilterResolutions(conflictingAccountName: string): string {
    return conflictingAccountName;
  }

  ConnectConnectIcon(): string {
    return 'third_party_xero_connect';
  }

  // callback that is called via pop-up when OAuth has been completed
  async Connected(info: any): Promise<void> {
    const companyId: string = this.companyService.CompanyId();

    try {
      await ApiService.put(
        `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/xero/oauth`,
        { body: info }
      );
    } catch (error) {
      if ((error as ApiErrorResponse).type === 'invalid.companySelection') {
        // This will show if you don't select a new organisation
        titleMessageLinkModal.show({
          title: 'Please Select An Organisation',
          optionText: 'View "Reconnecting to Xero" Article',
          optionLink:
            'https://www.plooto.com/support/s/article/connect-xero-to-your-plooto-account',
          message:
            'You must select a new Xero organisation to connect to. Each Xero organisation can only be connected to ONE Plooto company. ' +
            'If your organisation was previously connected or is not selectable, disconnect your Xero company and connect it again. ' +
            'Please read the "Reconnecting to Xero" section in the "Connect Xero to Plooto" help article below.',
          svgIconSymbolLarge: 'svg-warning-large',
        });
      }
      return;
    }

    this.fireAccountingSoftwareConnectedEvent();

    analytics.send('event', 'action', 'completed', 'Connected Xero');
    analytics.send('event', 'Company-AccountingSoftware', 'Connect', 'Xero');

    // if accounting software has re-connected manually trigger mutation
    this.companyService.membership().company().accountingSoftware.valueHasMutated?.();
  }

  // called via callback once user clicks on "connect" button
  async Connect(): Promise<any> {
    const companyId: string = this.companyService.CompanyId();

    const oauthData = await ApiService.get<IOAuthRequest>(
      `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/xero/oauth`
    );

    // set global variable for window so it can be accessed via "window.opener" by callback url
    // @ts-expect-error: Property 'XeroAccountingPackage' does not exist on type 'Window & typeof globalThis'.
    window.XeroAccountingPackage = this;

    const popup = setupPopupWindow({ width: 750, height: 810 });
    if (popup) {
      popup.location.href = oauthData.url;
    }
  }

  // generates a clickable link that starts connection process
  GenerateConnectButton($target: JQuery, context?: string): void {
    $('<a/>', {
      href: '#',
      class: 'third_party_xero_connect',
    })
      .appendTo($target)
      .click(() => {
        this.fireAccountingSoftwareConnectingEvent(context);
        this.Connect();
        return false;
      });
  }

  async AddAccount(account: IAccount): Promise<IAccount> {
    const companyId: string = this.companyService.CompanyId();

    const response = await ApiService.put<IAccount>(
      `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/xero/accounts`,
      { body: account }
    );

    return response;
  }

  async PostSettings(
    xeroAccountingSoftwareSettings: XeroAccountingSoftwareSettings
  ): Promise<boolean> {
    const companyId: string = this.companyService.CompanyId();

    const data = await ApiService.post<boolean>(
      `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/xero/settings`,
      { body: xeroAccountingSoftwareSettings }
    );

    return data;
  }

  async GetContacts(): Promise<Array<AccountingSoftwareContact>> {
    const companyId: string = this.companyService.CompanyId();

    const contacts = await ApiService.get<Array<AccountingSoftwareContact>>(
      `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/xero/contacts`
    );

    return contacts;
  }

  async GetAccounts(): Promise<Array<IAccount>> {
    const companyId: string = this.companyService.CompanyId();

    const accounts = await ApiService.get<Array<IAccount>>(
      `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/xero/accounts`
    );

    return accounts;
  }

  async fetchCompany(): Promise<AccountingSoftwareCompany> {
    const companyId: string = this.companyService.CompanyId();

    const response = await ApiService.get<AccountingSoftwareCompany>(
      `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/xero/company`
    );

    return response;
  }

  async GetBills(): Promise<Array<Bill>> {
    const companyId: string = this.companyService.CompanyId();

    const billData = await ApiService.get<Array<BillData>>(
      `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/xero/bills`
    );

    if (companyId !== this.companyService.CompanyId()) {
      throw new Error('Company switched while fetching bills');
    }

    return billData.map((data) => new Bill(data));
  }

  async GetInvoices(): Promise<Array<Invoice>> {
    const companyId: string = this.companyService.CompanyId();

    const invoiceData = await ApiService.get<Array<InvoiceData>>(
      `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/xero/invoices`
    );

    if (companyId !== this.companyService.CompanyId()) {
      throw new Error('Company switched while fetching invoices');
    }

    return invoiceData.map((data) => new Invoice(data));
  }

  GetBillAttachment(attachment: IBillAttachmentDetails) {
    const popup = setupPopupWindow({ width: 800, height: 650 });
    if (popup) {
      popup.location.href = attachment.url;
    }
  }

  async GetReconciliationStatus(): Promise<string> {
    return 'Reconciled';
  }

  async getFullVendorDetails(vendorDetails: any): Promise<any> {
    // Xero requires an additional call to fetch full vendor details
    const url = `${
      import.meta.env.APP_URLS_API_URL
    }/${API_PREFIX}/companies/${companyService.Instance.CompanyId()}/xero/vendors/${
      vendorDetails.id
    }`;
    const data = ApiService.get(url);
    return data;
  }

  async getFullCustomerDetails(customerDetails: any): Promise<any> {
    // Xero requires an additional call to fetch full vendor details
    const url = `${
      import.meta.env.APP_URLS_API_URL
    }/${API_PREFIX}/companies/${companyService.Instance.CompanyId()}/xero/customers/${
      customerDetails.id
    }`;
    const data = await ApiService.get(url);
    return data;
  }
}

export default XeroAccountingPackage;
